<template>
  <ZDataList
    ref="zList"
    :model-value="ZListValue"
    :items="list"
    :pinned-items="pinned"
    item-key="id"
    class="z-list"
    @update:model-value="goToIncident($event)">
    <template #action>
      <HeaderLeftContainer :title="$t(`domain.entities.incidents`)" :back-to="{ name: 'default' }">
        <v-btn icon variant="plain" @click="reloadIncidentList">
          <v-icon>mdi-reload</v-icon>
          <v-tooltip activator="parent" location="start">{{ $t('common.actions.reload') }}</v-tooltip>
        </v-btn>
      </HeaderLeftContainer>
    </template>
    <template #prepend-search>
      <sortIncidentsMenu v-model="selectedOrder" />
    </template>

    <template v-if="authStore.isAuthenticated" #append-search>
      <v-badge v-model="isFiltered" dot offset-x="10" offset-y="10" color="info">
        <v-btn icon flat variant="plain" @click="goToFilter()">
          <v-icon>mdi-tune</v-icon>
          <v-tooltip activator="parent" location="end">{{ $t('common.actions.filter') }}</v-tooltip>
        </v-btn>
      </v-badge>
    </template>

    <template #element="{ item }">
      <IncidentListItem :item="item"></IncidentListItem>
    </template>
  </ZDataList>
  <v-btn
    v-if="authStore.hasPermission('incident-create')"
    icon
    color="primary"
    size="large"
    class="left-panel-add-button"
    :to="{ name: `incidentnew` }">
    <v-icon>mdi-plus</v-icon>
    <v-tooltip activator="parent" location="bottom">{{ $t('common.actions.addIncident') }}</v-tooltip>
  </v-btn>
</template>

<script lang="ts" setup>
// Store import
import SettingsStore from '@/stores/settingsStore'
import FiltersStore from '@/stores/FiltersStore'
import AuthStore from '@/stores/auth'

const settings = SettingsStore()
const filtersStore = FiltersStore()
const authStore = AuthStore()

//FrameWork Imports
import { onUnmounted, ref, watch } from 'vue'

import { storeToRefs } from 'pinia'

//Types Imports

//Components Imports

//Utils Imports

// Services Imports

const { updateQueryValue, removeQueryValue, queryModelValue } = useQueryNavigation()
const queryIncidentValue = queryModelValue('incident')
const { pinned } = storeToRefs(incidentsPinnedStore())

const { filtersOpen, filters, isFiltered, searchText } = storeToRefs(filtersStore)

// Emitters

import { asyncComputed, until } from '@vueuse/core'

import { useQueryNavigation } from '@tsyl/vueabuse'
import { ZDataList } from '@tsyl/zapan2'
import type { ZIncidentListItemIncident } from '@tsyl/zapan2/lib/types/ZIncidentListItem'
import type { PaginateOptions } from '@tsyl/zapan2/lib/types/ZListTable'

import HeaderLeftContainer from '@/components/HeaderLeftContainer.vue'
import IncidentListItem from '@/components/Incident/IncidentListItem.vue'
import sortIncidentsMenu from '@/components/sortIncidentsMenu.vue'
import SortOptions from '@/constants/SortOptions'
import type { SortOptionType } from '@/constants/SortOptions'
import { getIncidents } from '@/services/api/incidents'
import { getMeassurements } from '@/services/api/masterData'
import { incidentsPinnedStore } from '@/stores/pinnedIncidents'
import emitter from '@/utils/EventBus'
import { transformIncidentApiToZIncident } from '@/utils/incidentTools'

const zList = ref()

const incidentMeasurements = asyncComputed(async () => {
  return await getMeassurements()
})

const reloadOnSave = () => zList.value.doReset()
emitter.on('save', reloadOnSave)
onUnmounted(() => emitter.off('save', reloadOnSave))

const selectedOrder = ref<SortOptionType>(SortOptions[2])
const ZListValue = ref<ZIncidentListItemIncident[]>([])
const listedItems = ref<ZIncidentListItemIncident[]>([])

async function list(paginateOptions: PaginateOptions, query: string) {
  await authStore.waitUntilInitialised()
  const itemsPromise = getIncidents({
    limit: paginateOptions.limit,
    offset: paginateOptions.offset,
    sortBy: selectedOrder.value.name.split('|')[0],
    desc: !!selectedOrder.value.name.split('|')[1],
    search: query,
    filters: filters.value
  })
  searchText.value = query
  const measurementsPromise = until(incidentMeasurements).toMatch((v) => !!v)
  const [items] = await Promise.all([itemsPromise, measurementsPromise])
  const incidents = items.elements
    .map((i) => transformIncidentApiToZIncident(i, incidentMeasurements.value))
    .map((incident) => {
      incident.secondLine = `${incident.secondLine} ${incident.discoveryTimestamp ? settings.formatStringDateTime(incident.discoveryTimestamp) : '-'}`
      return incident
    })
  listedItems.value = incidents
  return { items: incidents, total: items.total }
}

async function reloadIncidentList() {
  zList.value.doReset()
}

async function loadIncident(incidentId: string) {
  await updateQueryValue('incident', String(incidentId))
  filtersOpen.value = false
}

async function goToIncident(incidents: ZIncidentListItemIncident[]) {
  ZListValue.value = incidents
  const incident = incidents[0]
  if (incident) {
    await loadIncident(incident.id)
  }
}

function goToFilter() {
  ZListValue.value = []
  removeQueryValue(['incident'])
  filtersOpen.value = true
}

watch(
  () => [filters.value],
  (val) => {
    reloadIncidentList()
  }
)

watch(
  () => [selectedOrder.value],
  (val) => {
    reloadIncidentList()
  }
)

watch(
  () => listedItems.value,
  (val) => {
    if (!queryIncidentValue.value) return
    const found = val.find((x) => x.id === queryIncidentValue.value)
    if (found) ZListValue.value = [found]
  }
)

watch(
  () => queryIncidentValue.value,
  async (val) => {
    if (!val || ZListValue.value.length > 0) return
    // await until(listedItems).toMatch((v) => v.some((x) => x.id === val))
    await until(listedItems).toMatch((v) => !!v.length)
    const found = listedItems.value.find((x) => x.id === val)
    if (found) await goToIncident([found])
    else await loadIncident(val)
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
.stickyPinned {
  position: sticky;
  top: 0;
  z-index: 100;
}

.z-list {
  height: 100%;
  background-color: rgb(var(--v-theme-surface)) !important;
}

:deep(.searchable-list__header) {
  background-color: rgb(var(--v-theme-surface-light)) !important;
}
</style>
